// components/SearchableSelect.jsx — dropdown dengan kotak cari, pengganti
// <select> native untuk daftar panjang (instance_type bisa 400+ entri).
//
// KENAPA BUKAN <select> BIASA. Scroll manual di antara ratusan instance type
// lambat dan gampang salah pilih tetangganya. Ketik untuk menyaring jauh lebih
// cepat — dan tetap dapat dipakai keyboard penuh (panah, Enter, Escape) untuk
// yang tidak mau pakai mouse.
//
// STATELESS TERHADAP DATA. Komponen ini tidak tahu apa-apa soal AWS; ia
// menerima array `options` generik dan memanggil `onChange(value)`. Dipakai
// ulang di kalkulator (instance_type, os, tenancy, pre_installed_sw,
// usage_type) maupun katalog (filter apa pun yang daftarnya panjang).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';

export default function SearchableSelect({
  value, onChange, options, placeholder, disabled, allLabel, formatOption,
  // `wide` melepaskan lebar panel dari lebar tombolnya. Diperlukan untuk daftar
  // yang labelnya panjang secara struktural: usage type AWS bisa 196 karakter
  // (service · usage_type · operation — deskripsi rute), dan di kolom sempit
  // seluruhnya terpotong sehingga pilihan tidak bisa dibedakan. Memperkecil
  // font tidak menyelesaikannya — yang kurang adalah ruang, bukan ukuran huruf.
  // `clearable` menampilkan tombol × pada trigger: menghapus pilihan tanpa
  // harus mencari entri kosong di dalam daftar.
  clearable,
  wide,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  // Normalisasi: terima array string ATAU array {value,label,count}.
  const norm = useMemo(() => (options || []).map(o =>
    (typeof o === 'string' ? { value: o, label: o } : o)
  ), [options]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return norm;
    // Baris kedua ikut dicari. Untuk data transfer, yang dihafal orang justru
    // tujuannya ('Frankfurt'), bukan kode usage type-nya.
    return norm.filter(o =>
      String(o.label).toLowerCase().includes(q)
      || String(o.sub || '').toLowerCase().includes(q));
  }, [norm, query]);

  const selected = norm.find(o => o.value === value) || null;

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setHighlight(0);
      // Fokus kotak cari begitu dropdown terbuka, supaya pengguna bisa
      // langsung ketik tanpa klik lagi.
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const commit = (v) => {
    onChange(v);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, filtered.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); return; }
    if (e.key === 'Enter') {
      e.preventDefault();
      const opt = filtered[highlight];
      if (opt && !opt.disabled) commit(opt.value);
      return;
    }
  };

  const label = (o) => (formatOption ? formatOption(o) : o.label);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        className={`w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white text-left flex items-center justify-between gap-1 disabled:bg-gray-100 disabled:text-gray-400 ${
          open ? 'ring-1 ring-emerald-500 border-emerald-500' : ''}`}
      >
        <span className={`truncate ${selected ? 'text-gray-800' : 'text-gray-400'}`}
              title={selected ? [label(selected), selected.sub].filter(Boolean).join(' — ') : undefined}>
          {selected ? label(selected) : (placeholder || '—')}
        </span>
        <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" />
      </button>

      {/* Tombol clear diletakkan DI LUAR trigger: <button> di dalam <button>
          bukan HTML yang sah dan klik-nya tidak sampai ke handler. */}
      {clearable && value && !disabled && (
        <button
          type="button"
          title="Clear"
          onClick={(e) => { e.stopPropagation(); onChange(''); }}
          className="absolute right-6 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}

      {open && (
        <div className={`absolute z-20 mt-1 min-w-[220px] rounded-lg border border-gray-200 bg-white shadow-lg ${
          wide ? 'w-[34rem] max-w-[calc(100vw-3rem)]' : 'w-full'}`}>
          <div className="p-1.5 border-b border-gray-100 relative">
            <Search className="w-3.5 h-3.5 absolute left-3.5 top-3.5 text-gray-400" />
            <input
              ref={inputRef}
              value={query}
              onChange={e => { setQuery(e.target.value); setHighlight(0); }}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              className="w-full pl-7 pr-2 py-1.5 rounded border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>
          <ul className={`overflow-y-auto py-1 ${wide ? 'max-h-80' : 'max-h-56'}`}>
            {allLabel && !query && (
              <li>
                <button
                  type="button"
                  onClick={() => commit('')}
                  className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 ${!value ? 'text-emerald-700 font-medium' : 'text-gray-600'}`}
                >
                  {allLabel}
                </button>
              </li>
            )}
            {!filtered.length && (
              <li className="px-3 py-2 text-xs text-gray-400">—</li>
            )}
            {filtered.map((o, i) => (
              <li key={o.value}>
                <button
                  type="button"
                  disabled={o.disabled}
                  title={o.disabled ? (o.disabledHint || 'Not available for the current combination') : undefined}
                  onClick={() => commit(o.value)}
                  onMouseEnter={() => setHighlight(i)}
                  className={`w-full text-left px-3 py-1.5 text-sm flex items-start justify-between gap-2 ${
                    i === highlight && !o.disabled ? 'bg-emerald-50' : ''} ${
                    o.disabled
                      ? 'text-gray-300 cursor-not-allowed'
                      : (o.value === value ? 'text-emerald-700 font-medium' : 'text-gray-700')}`}
                >
                  {/* Dua baris bila opsi punya `sub`: baris atas identitas
                      teknis, baris bawah keterangan. Satu baris terpotong
                      membuat dua rute berbeda tampak identik. */}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{label(o)}</span>
                    {o.sub && <span className="block truncate text-xs text-gray-500 font-normal">{o.sub}</span>}
                  </span>
                  {o.count !== undefined && <span className="text-xs text-gray-400 shrink-0">{o.count}</span>}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
